import { Daycare } from '@/types/Daycare.ts'
import { FunctionComponent, JSX, useEffect, useState } from 'react'
import styles from '@/container/daycare/daycareProfile/meal/meal.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useTranslation } from 'react-i18next'
import { MealTexture } from '@/types/Meal.ts'
import { GET_DAYCARE_MEAL_TEXTURES } from '@/modules/API/meal/getMealDaycareSettings.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { Chip, ListItem } from '@mui/material'
import { Delete, Edit } from '@mui/icons-material'
import { useLazyQuery } from '@apollo/client'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'
import AddMealTextureDialog from '@/container/daycare/daycareProfile/meal/mealTextures/AddMealTextureDialog.tsx'
import UpdateMealTextureDialog from '@/container/daycare/daycareProfile/meal/mealTextures/UpdateMealTextureDialog.tsx'
import ConfirmDeleteMealTextureDialog from '@/container/daycare/daycareProfile/meal/mealTextures/ConfirmDeleteMealTextureDialog.tsx'

interface MealTexturesProps {
    daycare: Daycare
    canUpdate: boolean
}

const MealTextures: FunctionComponent<MealTexturesProps> = ({ daycare, canUpdate }: MealTexturesProps) => {
    const { t } = useTranslation()

    const [addMealTextureDialog, setAddMealTextureDialog] = useState<boolean>(false)
    const [editTexture, setEditTexture] = useState<MealTexture | null>(null)
    const [deleteTexture, setDeleteTexture] = useState<MealTexture | null>(null)

    const [textures, setTextures] = useState<MealTexture[]>([])

    const [getMealTextures, { loading, data, error, refetch }] = useLazyQuery(GET_DAYCARE_MEAL_TEXTURES, {
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (data) {
            setTextures(data.getDaycareMealTextures)
        }
    }, [data])

    useEffect(() => {
        getMealTextures()
    }, [daycare.id])

    return (
        <div className={styles.BlockPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myMealTextures')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddMealTextureDialog(true)}
                        />
                    )}
                </div>
                {loading && <Loading overall={false} />}
                {error && <ErrorMessage error={t('generalError')} />}
                {textures && textures.length > 0 ? (
                    <div className={styles.InputContainer}>
                        {[...textures]
                            .sort((a: MealTexture, b: MealTexture): number => a.texture.localeCompare(b.texture))
                            .map(
                                (data: MealTexture): JSX.Element => (
                                    <ListItem key={data.id} className={styles.ListItem}>
                                        <Chip
                                            className={styles.ChipDefault}
                                            label={
                                                <span className={styles.ChipLabel}>
                                                    <span>{data.texture}</span>
                                                    {canUpdate && (
                                                        <>
                                                            <Edit
                                                                className={styles.ChipActionIcon}
                                                                onClick={(_e) => setEditTexture(data)}
                                                            />
                                                            <Delete
                                                                className={styles.ChipActionIcon}
                                                                onClick={(_e) => setDeleteTexture(data)}
                                                            />
                                                        </>
                                                    )}
                                                </span>
                                            }
                                        />
                                    </ListItem>
                                )
                            )}
                    </div>
                ) : (
                    <p>{t('noMealTextures')}</p>
                )}
            </div>
            <AddMealTextureDialog
                open={addMealTextureDialog}
                handleClose={() => setAddMealTextureDialog(false)}
                refetch={refetch}
                daycareId={daycare.id}
            />
            {editTexture && (
                <UpdateMealTextureDialog
                    open={!!editTexture}
                    handleClose={() => setEditTexture(null)}
                    refetch={refetch}
                    daycareId={daycare.id}
                    texture={editTexture}
                />
            )}
            {deleteTexture && (
                <ConfirmDeleteMealTextureDialog
                    open={!!deleteTexture}
                    handleClose={() => setDeleteTexture(null)}
                    daycareId={daycare.id}
                    texture={deleteTexture}
                    refetch={refetch}
                />
            )}
        </div>
    )
}

export default MealTextures
